import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useKiosk } from '../../contexts/KioskContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Switch from '../ui/Switch'
import toast from 'react-hot-toast'

const { FiTablet, FiLock, FiUnlock, FiSettings, FiClock, FiUsers, FiShield } = FiIcons

const KioskModeToggle = () => {
  const { family } = useAuth()
  const { kioskSettings, updateKioskSettings, isKioskMode, enterKioskMode } = useKiosk()
  const [showPinSetup, setShowPinSetup] = useState(false)
  const [pinInput, setPinInput] = useState('')
  const [confirmPin, setConfirmPin] = useState('')

  const handleEnableKiosk = () => {
    if (!kioskSettings.parentPin) {
      setShowPinSetup(true)
      return
    }
    updateKioskSettings({ enabled: true })
    toast.success('Kiosk mode enabled! 🎮')
  }

  const handleDisableKiosk = () => {
    updateKioskSettings({ enabled: false })
    toast.success('Kiosk mode disabled')
  }

  const handleSetupPin = () => {
    if (!pinInput || pinInput.length < 4) {
      toast.error('PIN must be at least 4 characters')
      return
    }

    if (pinInput !== confirmPin) {
      toast.error('PINs do not match')
      return
    }

    updateKioskSettings({
      parentPin: pinInput,
      enabled: true
    })

    setPinInput('')
    setConfirmPin('')
    setShowPinSetup(false)
    toast.success('Kiosk mode enabled with PIN protection! 🔐')
  }

  const handleStartKiosk = () => {
    enterKioskMode()
  }

  if (!family?.children || family.children.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Add Children First
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need to add children to your family before enabling kiosk mode.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Main Toggle */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiTablet} className="text-white text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Kiosk Mode</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Let kids use the app independently without parent supervision
              </p>
            </div>
          </div>
          <Switch
            checked={kioskSettings.enabled}
            onChange={(enabled) => {
              if (enabled) {
                handleEnableKiosk()
              } else {
                handleDisableKiosk()
              }
            }}
            size="lg"
          />
        </div>

        {kioskSettings.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="border-t border-gray-200 dark:border-gray-700 pt-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SafeIcon 
                  icon={isKioskMode ? FiUnlock : FiLock} 
                  className={`text-lg ${isKioskMode ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'}`} 
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Status: {isKioskMode ? 'Active' : 'Ready'}
                </span>
              </div>
              {!isKioskMode && (
                <Button variant="primary" size="sm" onClick={handleStartKiosk}>
                  <SafeIcon icon={FiTablet} className="text-sm" />
                  Start Kiosk Mode
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </Card>

      {/* PIN Setup Modal */}
      {showPinSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700"
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiLock} className="text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Setup Parent PIN</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Create Parent PIN *
                  </label>
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter 4+ digit PIN"
                    maxLength={10}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm PIN *
                  </label>
                  <input
                    type="password"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Confirm your PIN"
                    maxLength={10}
                  />
                </div>

                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    <strong>Important:</strong> This PIN will be required to exit kiosk mode and access parent features. Keep it secure!
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setShowPinSetup(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSetupPin}
                    className="flex-1"
                    disabled={!pinInput || pinInput !== confirmPin}
                  >
                    <SafeIcon icon={FiLock} className="text-sm" />
                    Setup PIN
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Kiosk Settings */}
      {kioskSettings.enabled && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Kiosk Settings</h3>
          <div className="space-y-4">
            {/* Auto-logout */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiClock} className="text-blue-500 dark:text-blue-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Auto-logout Timer</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Minutes of inactivity before auto-logout
                  </div>
                </div>
              </div>
              <select
                value={kioskSettings.autoLogoutMinutes}
                onChange={(e) => updateKioskSettings({ autoLogoutMinutes: parseInt(e.target.value) })}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={0}>Never</option>
              </select>
            </div>

            {/* Task Self-Assignment */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiUsers} className="text-green-500 dark:text-green-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Task Self-Assignment</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Allow kids to assign unassigned tasks to themselves
                  </div>
                </div>
              </div>
              <Switch
                checked={kioskSettings.allowTaskSelfAssignment}
                onChange={(checked) => updateKioskSettings({ allowTaskSelfAssignment: checked })}
              />
            </div>

            {/* Reward Requests */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiShield} className="text-purple-500 dark:text-purple-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Reward Requests</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Allow kids to request reward redemptions
                  </div>
                </div>
              </div>
              <Switch
                checked={kioskSettings.allowRewardRequests}
                onChange={(checked) => updateKioskSettings({ allowRewardRequests: checked })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Quick Start Guide */}
      {kioskSettings.enabled && !isKioskMode && (
        <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border-primary-200 dark:border-primary-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            How to Use Kiosk Mode
          </h3>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary-600 dark:text-primary-400">1.</span>
              <span>Click "Start Kiosk Mode" to begin a kiosk session</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary-600 dark:text-primary-400">2.</span>
              <span>Kids will see a child selection screen to pick their profile</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary-600 dark:text-primary-400">3.</span>
              <span>Children can complete tasks, assign tasks to themselves, and request rewards</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary-600 dark:text-primary-400">4.</span>
              <span>Use your PIN to exit kiosk mode and return to parent controls</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

export default KioskModeToggle